import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { TimeSpan } from '@vendasta/meetings';
import { MeetingSchedulerStoreService } from '../../../data-providers/meeting-scheduler-store.service';
import {
  formatDateWithoutTimezone,
  formatDate,
  createDateValidator,
  getMonthKey,
  calculateCalendarDateEnd,
  getStartOfMonth,
  getEndOfMonth,
  pushToMapList,
} from '../../calendar-utils';

export interface CalendarConfig {
  calendarId: string;
  meetingTypeId: string;
  timezone: string;
  userID: string;
  // Custom date range support
  hasCustomDateRange?: boolean;
  customDateRangeStart?: Date | null;
  customDateRangeEnd?: Date | null;
  eventTypeEndDateInPast?: boolean;
}

@Component({
  selector: 'meeting-scheduler-calendar-date-picker',
  templateUrl: './calendar-date-picker.component.html',
  styleUrls: ['./calendar-date-picker.component.scss'],
  standalone: false,
})
export class CalendarDatePickerComponent implements OnInit, OnDestroy, OnChanges {
  @Input() config: CalendarConfig | null = null;
  @Input() selectedDate: Date | null = null;
  @Input() formControlName = 'date';
  @Input() placeholder = 'Select date';
  @Input() label = 'Date';

  @Output() dateChange = new EventEmitter<Date | null>();
  @Output() availableTimeSlotsChange = new EventEmitter<string[]>();
  @Output() calendarStateChange = new EventEmitter<{
    isDisabled: boolean;
    dateStart: Date | null;
    dateEnd: Date | null;
  }>();

  // Calendar state
  private dateTimeMap: Map<string, string[]> = new Map();
  private monthlyDateTimeMap = new Map<string, string[]>();
  private subscription: Subscription[] = [];

  calenderDateStart: Date | null = null;
  calenderDateEnd: Date | null = null;
  isCalenderDisabled = true;
  validateDateFn: (d: Date | null) => boolean = () => false;

  // Navigation state
  currentMonth: Date = new Date();
  maxMonths = 12;

  constructor(private readonly meetingSchedulerStoreService: MeetingSchedulerStoreService) {
    this.calenderDateEnd = calculateCalendarDateEnd(this.maxMonths);
  }

  ngOnInit(): void {
    this.validateDateFn = createDateValidator(this.dateTimeMap);
    this.emitCalendarState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && this.config) {
      const previousConfig = changes['config'].previousValue;
      const currentConfig = changes['config'].currentValue;

      if (!previousConfig || this.hasConfigurationChanged(previousConfig, currentConfig)) {
        this.resetCalendarAndFetch();
      }
    }

    if (changes['selectedDate'] && this.selectedDate) {
      this.updateAvailableTimeSlots();
    }
  }

  private hasConfigurationChanged(previous: CalendarConfig, current: CalendarConfig): boolean {
    if (!previous || !current) return true;

    return (
      previous.calendarId !== current.calendarId ||
      previous.meetingTypeId !== current.meetingTypeId ||
      previous.timezone !== current.timezone ||
      previous.userID !== current.userID ||
      previous.hasCustomDateRange !== current.hasCustomDateRange ||
      previous.eventTypeEndDateInPast !== current.eventTypeEndDateInPast ||
      previous.customDateRangeStart?.getTime() !== current.customDateRangeStart?.getTime() ||
      previous.customDateRangeEnd?.getTime() !== current.customDateRangeEnd?.getTime()
    );
  }

  ngOnDestroy(): void {
    this.subscription.forEach((sub) => sub.unsubscribe());
    this.subscription = [];
  }

  onDateChange(date: Date | null): void {
    this.selectedDate = date;
    this.dateChange.emit(date);
    this.updateAvailableTimeSlots();
  }

  private updateAvailableTimeSlots(): void {
    if (!this.selectedDate) {
      this.availableTimeSlotsChange.emit([]);
      return;
    }

    const availableSlots = this.dateTimeMap.get(formatDateWithoutTimezone(this.selectedDate)) || [];
    this.availableTimeSlotsChange.emit(availableSlots);
  }

  private resetCalendarAndFetch(): void {
    if (!this.config?.timezone) return;

    this.monthlyDateTimeMap = new Map<string, string[]>();
    this.dateTimeMap = new Map<string, string[]>();

    // For custom date ranges, start from the custom start date
    if (this.config.hasCustomDateRange && this.config.customDateRangeStart) {
      this.currentMonth = new Date(this.config.customDateRangeStart);
      this.currentMonth.setDate(1); // Set to first day of the month
    } else {
      this.currentMonth = new Date();
    }

    this.fetchCalendarAvailabilityForMonth(this.currentMonth);
  }

  fetchCalendarAvailabilityForMonth(monthStart: Date): void {
    if (!this.config) return;

    // Check if event type end date is in the past - skip API call
    if (this.config.eventTypeEndDateInPast) {
      this.isCalenderDisabled = true;
      this.emitCalendarState();
      return;
    }

    const monthKey = getMonthKey(monthStart);

    // Skip if data already cached
    if (this.monthlyDateTimeMap.has(monthKey)) return;

    this.isCalenderDisabled = true;
    this.emitCalendarState();

    const now = new Date();
    const startOfMonth = getStartOfMonth(monthStart.getFullYear(), monthStart.getMonth());
    const endOfMonth = getEndOfMonth(monthStart.getFullYear(), monthStart.getMonth());

    const start = now > startOfMonth ? now : startOfMonth;
    const end = endOfMonth > this.calenderDateEnd! ? this.calenderDateEnd! : endOfMonth;

    const timeRange: TimeSpan = { start, end };

    const response = this.meetingSchedulerStoreService.loadAvailableTimeSlots({
      calendarId: this.config.calendarId,
      meetingTypeId: this.config.meetingTypeId,
      timeZone: { id: this.config.timezone },
      userID: this.config.userID,
      timeSpan: timeRange,
    });

    this.subscription.push(
      response.subscribe((availableTimeSlots) => {
        const monthDateMap = new Map<string, string[]>();

        for (const slot of availableTimeSlots) {
          const date = formatDate(slot.start, this.config!.timezone); // YYYY-MM-DD
          const time = new Date(slot.start).toLocaleTimeString('en-US', {
            timeZone: this.config!.timezone,
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          });

          pushToMapList(monthDateMap, date, time);
          pushToMapList(this.dateTimeMap, date, time);
        }

        // Cache flat times for this month
        const flatTimes = Array.from(monthDateMap.entries()).reduce<string[]>(
          (acc, [, times]) => acc.concat(times),
          [],
        );
        this.monthlyDateTimeMap.set(monthKey, flatTimes);

        const allDates = Array.from(this.dateTimeMap.keys()).sort();
        if (allDates.length) {
          this.calenderDateStart = new Date(allDates[0]);
        }

        this.finishLoading();
      }),
    );
  }

  private finishLoading(): void {
    this.isCalenderDisabled = false;
    this.validateDateFn = createDateValidator(this.dateTimeMap);
    this.emitCalendarState();
    this.updateAvailableTimeSlots(); // Update slots for current selection
  }

  private emitCalendarState(): void {
    this.calendarStateChange.emit({
      isDisabled: this.isCalenderDisabled,
      dateStart: this.calenderDateStart,
      dateEnd: this.calenderDateEnd,
    });
  }

  onNextMonth(): void {
    const nextMonth = new Date(this.currentMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    let maxAllowedDate: Date;

    if (this.config?.hasCustomDateRange && this.config.customDateRangeEnd) {
      // For custom date ranges, use the custom end date as the limit
      maxAllowedDate = new Date(this.config.customDateRangeEnd);
    } else {
      // For non-custom date ranges, use the default 12-month window
      maxAllowedDate = new Date(Date.now());
      maxAllowedDate.setMonth(maxAllowedDate.getMonth() + this.maxMonths);
    }

    // Don't allow navigation beyond the maximum date
    if (nextMonth > maxAllowedDate) return;

    this.currentMonth = nextMonth;
    this.fetchCalendarAvailabilityForMonth(nextMonth);
  }

  onPrevMonth(): void {
    const prevMonth = new Date(this.currentMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);

    let minAllowedDate: Date;

    if (this.config?.hasCustomDateRange && this.config.customDateRangeStart) {
      // For custom date ranges, use the custom start date as the limit
      const customStartMonth = new Date(
        this.config.customDateRangeStart.getFullYear(),
        this.config.customDateRangeStart.getMonth(),
        1,
      );
      const now = new Date(Date.now());
      const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      // If custom start was adjusted to current time (due to past date), use current month as limit
      minAllowedDate = customStartMonth < currentMonth ? currentMonth : customStartMonth;
    } else {
      // For non-custom date ranges, don't go before current month
      const now = new Date(Date.now());
      minAllowedDate = new Date(now.getFullYear(), now.getMonth(), 1);
    }

    // Don't allow navigation before the minimum date
    if (prevMonth < minAllowedDate) return;

    this.currentMonth = prevMonth;
    this.fetchCalendarAvailabilityForMonth(prevMonth);
  }
}
